import React, { useEffect, useMemo, useState } from "react";
import { MenuItem, Select, FormControl, InputLabel, Stack, TextField, Box } from "@mui/material";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  SRS_FORM_OPTIONS,
  type Srs2FormData,
  type SrsFormAdministered,
} from "../../types";
import { space3, space5 } from "../../styled";
import { getSrs2Severity, getSrs2TotalSeverity } from "../../utils";
import { AssessmentDatePicker, FormActionRow } from "./shared";
import { getUser } from "selectors";

interface Srs2DataEntryProps {
  administratorName: string;
  onCancel: () => void;
  onSave: (data: Srs2FormData) => void;
}

interface TScoreField {
  scoreKey: keyof Pick<
    Srs2FormData,
    | "awarenessTScore"
    | "cognitionTScore"
    | "communicationTScore"
    | "motivationTScore"
    | "srsSciTScore"
    | "rirbTScore"
  >;
  label: string;
  severityLabel: string;
}

const T_SCORE_FIELDS: TScoreField[] = [
  {
    scoreKey: "awarenessTScore",
    label: "Social awareness T-score",
    severityLabel: "Awareness Level",
  },
  {
    scoreKey: "cognitionTScore",
    label: "Social cognition T-score",
    severityLabel: "Cognition Level",
  },
  {
    scoreKey: "communicationTScore",
    label: "Social communication T-score",
    severityLabel: "Communication Level",
  },
  {
    scoreKey: "motivationTScore",
    label: "Social motivation T-score",
    severityLabel: "Motivation Level",
  },
  {
    scoreKey: "srsSciTScore",
    label: "SRS Social Communication and Interaction T-Score",
    severityLabel: "Social Communication and Interaction Level",
  },
  {
    scoreKey: "rirbTScore",
    label: "Restricted Interests and Repetitive Behavior T-Score",
    severityLabel: "Restricted Interests and Repetitive Behavior Level",
  },
];

/** Matches AssessmentDatePicker's own normalization (local noon, full ISO datetime). */
function todayAsAssessmentDate(): string {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
}

const INITIAL_FORM: Srs2FormData = {
  administrationDate: todayAsAssessmentDate(),
  administratorName: "",
  formAdministered: null,
  respondentName: null,
  relationshipToClient: null,
  totalTScore: null,
  awarenessTScore: null,
  cognitionTScore: null,
  communicationTScore: null,
  motivationTScore: null,
  srsSciTScore: null,
  rirbTScore: null,
};

/**
 * Data entry form for SRS-2 (Social Responsiveness Scale) assessment.
 * Collects form metadata, respondent info, and T-scores.
 * Severity/Level fields are auto-derived from T-scores (read-only, not stored in backend).
 */
export default function Srs2DataEntry({ onCancel, onSave }: Srs2DataEntryProps) {
  // @ts-expect-error — getUser selector return type doesn't declare `name`
  const currentUserName = useSelector(getUser)?.name ?? "";
  const [form, setForm] = useState<Srs2FormData>(() => ({
    ...INITIAL_FORM,
    administratorName: currentUserName,
  }));

  // Sync Redux user name to form whenever it changes (for auto-population)
  useEffect(() => {
    setForm((prev) => ({ ...prev, administratorName: currentUserName }));
  }, [currentUserName]);

  function setField<K extends keyof Srs2FormData>(key: K, value: Srs2FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // Total T-score is derived by the backend as the sum of the 6 subscale T-scores.
  // We compute it here for display only; it is NOT included in the payload.
  const derivedTotalTScore = useMemo(() => {
    const subscales = [
      form.awarenessTScore,
      form.cognitionTScore,
      form.communicationTScore,
      form.motivationTScore,
      form.srsSciTScore,
      form.rirbTScore,
    ];
    if (subscales.every((v) => v === null)) return null;
    return subscales.reduce<number>((sum, v) => sum + (v ?? 0), 0);
  }, [
    form.awarenessTScore,
    form.cognitionTScore,
    form.communicationTScore,
    form.motivationTScore,
    form.srsSciTScore,
    form.rirbTScore,
  ]);

  return (
    <Stack component="form" sx={{ height: "100%" }} noValidate>
      <Stack sx={{ overflowY: "auto", flex: 1, pr: 1 }} rowGap={space5}>
        <Stack rowGap={space3}>
          <TextField
            label="Administrator Name"
            size="small"
            value={form.administratorName}
            onChange={(e) => setField("administratorName", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="form-administered-label">Form administered</InputLabel>
            <Select
              labelId="form-administered-label"
              label="Form administered"
              value={form.formAdministered ?? ""}
              onChange={(e) =>
                setField("formAdministered", (e.target.value as SrsFormAdministered) || null)
              }
            >
              <MenuItem value="">Please select</MenuItem>
              {SRS_FORM_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AssessmentDatePicker
            value={form.administrationDate}
            onChange={(date) => setField("administrationDate", date)}
          />
        </Stack>

        <Stack rowGap={space3}>
          <TextField
            label="Respondent name"
            size="small"
            value={form.respondentName ?? ""}
            onChange={(e) => setField("respondentName", e.target.value || null)}
            placeholder="Rita Lopez"
          />

          <TextField
            label="Relationship to client"
            size="small"
            value={form.relationshipToClient ?? ""}
            onChange={(e) => setField("relationshipToClient", e.target.value || null)}
            placeholder="Mother"
          />
        </Stack>

        <Stack rowGap={space3}>
          {/* Total T-score — derived from subscales (backend computes it; display only) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 140px",
              gap: space3,
              alignItems: "center",
            }}
          >
            <Box>
              <Box component="div">
                <Box component="div" sx={{ typography: "body2" }}>
                  Total T-score
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <TextField
                size="small"
                placeholder="#"
                value={derivedTotalTScore ?? ""}
                InputProps={{ readOnly: true }}
                sx={{ width: 112 }}
                aria-label="Total T-score"
              />
              <Box sx={{ mt: 1 }}>
                <Box
                  component="div"
                  sx={{
                    typography: "body2",
                    color: "text.secondary",
                    fontStyle: "italic",
                    textAlign: "right",
                  }}
                >
                  {getSrs2TotalSeverity(derivedTotalTScore) ?? "—"}
                </Box>
              </Box>
            </Box>
          </Box>

          {T_SCORE_FIELDS.map((field) => (
            <Box
              key={field.scoreKey}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 140px",
                gap: space3,
                alignItems: "center",
              }}
            >
              <Box>
                <Box component="div" sx={{ typography: "body2" }}>
                  {field.label}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <TextField
                  placeholder="#"
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 200, step: 1 }}
                  value={form[field.scoreKey] ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setField(field.scoreKey, null);
                      return;
                    }
                    const parsed = Number(raw);
                    if (isNaN(parsed)) return;
                    const clamped = Math.min(Math.max(Math.round(parsed), 0), 200);
                    setField(field.scoreKey, clamped);
                  }}
                  sx={{ width: 112 }}
                  aria-label={`${field.label} T-Score`}
                />
                <Box sx={{ mt: 1 }}>
                  <Box
                    component="div"
                    sx={{
                      typography: "body2",
                      color: "text.secondary",
                      fontStyle: "italic",
                      textAlign: "right",
                    }}
                  >
                    {getSrs2Severity(form[field.scoreKey]) ?? "—"}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </Stack>
      <Box sx={{ pt: space3, flexShrink: 0 }}>
        <FormActionRow
          onCancel={onCancel}
          onSubmit={() => onSave({ ...form, totalTScore: derivedTotalTScore })}
          submitLabel="Add assessment"
        />
      </Box>
    </Stack>
  );
}
